import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/lib/api'

interface Movimiento {
  id: string
  tipo: string
  cantidad: number
  referencia_tipo: string | null
  notas: string | null
  created_at: string
  producto: { id: string; nombre: string } | null
  deposito: { id: string; nombre: string } | null
}

interface Deposito { id: string; nombre: string; activo: boolean }

const TIPO_LABELS: Record<string, { label: string; pill: string; signo: '+' | '-' }> = {
  entrada:          { label: 'Entrada', pill: 'me-pill-ok', signo: '+' },
  salida:           { label: 'Salida', pill: 'me-pill-bad', signo: '-' },
  ajuste:           { label: 'Ajuste', pill: 'me-pill-warn', signo: '+' },
  devolucion:       { label: 'Devolución', pill: 'me-pill-ok', signo: '+' },
}

const REF_LABELS: Record<string, string> = {
  venta:            'Venta',
  anulacion_venta:  'Anulación',
  compra:           'Compra',
  ajuste_manual:    'Ajuste manual',
  devolucion:       'Devolución',
}

function useMovimientos(params: { depositoId: string; tipo: string; desde: string; hasta: string }) {
  const qs = new URLSearchParams()
  if (params.depositoId) qs.set('deposito_id_filter', params.depositoId)
  return useQuery({
    queryKey: ['movimientos-admin', params],
    queryFn: () => api.get<Movimiento[]>(`/api/stock/movimientos?limit=300`),
    staleTime: 20_000,
  })
}

function useDepositos() {
  return useQuery({
    queryKey: ['depositos'],
    queryFn: () => api.get<Deposito[]>('/api/depositos?all=true'),
    staleTime: 60_000,
  })
}

export default function MovimientosPage() {
  const [filtroTipo, setFiltroTipo] = useState('')
  const [filtroDeposito, setFiltroDeposito] = useState('')
  const [busqueda, setBusqueda] = useState('')
  const [desde, setDesde] = useState(() => {
    const d = new Date(); d.setDate(d.getDate() - 7)
    return d.toISOString().slice(0, 10)
  })
  const [hasta, setHasta] = useState(() => new Date().toISOString().slice(0, 10))

  const { data: movimientos = [], isLoading } = useMovimientos({ depositoId: filtroDeposito, tipo: filtroTipo, desde, hasta })
  const { data: depositos = [] } = useDepositos()

  const filtrados = movimientos.filter(m => {
    if (filtroTipo && m.tipo !== filtroTipo) return false
    if (filtroDeposito && m.deposito?.id !== filtroDeposito) return false
    if (busqueda && !m.producto?.nombre.toLowerCase().includes(busqueda.toLowerCase())) return false
    const fecha = m.created_at.slice(0, 10)
    if (desde && fecha < desde) return false
    if (hasta && fecha > hasta) return false
    return true
  })

  const totales = filtrados.reduce((acc, m) => {
    const esEntrada = ['entrada', 'devolucion'].includes(m.tipo)
    acc.entradas += esEntrada ? Number(m.cantidad) : 0
    acc.salidas += m.tipo === 'salida' ? Number(m.cantidad) : 0
    return acc
  }, { entradas: 0, salidas: 0 })

  return (
    <div>
      <div style={{ marginBottom: 'var(--me-space-6)' }}>
        <div className="me-eyebrow" style={{ marginBottom: 'var(--me-space-2)' }}>Administracion</div>
        <h1 className="me-page-title">Registro de Movimientos</h1>
      </div>

      {/* KPI rápidos */}
      <div style={{ display: 'flex', gap: 'var(--me-space-4)', marginBottom: 'var(--me-space-5)', flexWrap: 'wrap' }}>
        <KpiCard label="Movimientos" value={filtrados.length.toString()} />
        <KpiCard label="Entradas" value={`+${totales.entradas.toLocaleString('es-AR')}`} color="var(--me-ok)" />
        <KpiCard label="Salidas" value={`-${totales.salidas.toLocaleString('es-AR')}`} color="var(--me-bad)" />
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 'var(--me-space-3)', flexWrap: 'wrap', marginBottom: 'var(--me-space-5)', alignItems: 'center' }}>
        <input className="me-input" style={{ width: 200 }} placeholder="Buscar producto..."
          value={busqueda} onChange={e => setBusqueda(e.target.value)} />
        <select className="me-input" style={{ width: 160 }} value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
          <option value="">Todos los tipos</option>
          {Object.entries(TIPO_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
        </select>
        {depositos.length > 0 && (
          <select className="me-input" style={{ width: 180 }} value={filtroDeposito} onChange={e => setFiltroDeposito(e.target.value)}>
            <option value="">Todos los depósitos</option>
            {depositos.map(d => <option key={d.id} value={d.id}>{d.nombre}</option>)}
          </select>
        )}
        <input className="me-input" type="date" style={{ width: 140 }} value={desde} onChange={e => setDesde(e.target.value)} />
        <span style={{ color: 'var(--me-text-4)', fontSize: 12 }}>a</span>
        <input className="me-input" type="date" style={{ width: 140 }} value={hasta} onChange={e => setHasta(e.target.value)} />
        <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--me-text-4)' }}>
          {filtrados.length} registro{filtrados.length !== 1 ? 's' : ''}
        </span>
      </div>

      {isLoading ? (
        <Skeleton />
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '48px 24px', color: 'var(--me-text-3)', fontSize: 13 }}>
          Sin movimientos para los filtros seleccionados
        </div>
      ) : (
        <div className="me-card" style={{ padding: 0, overflow: 'hidden' }}>
          <table className="me-table" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th>Producto</th>
                <th>Depósito</th>
                <th style={{ textAlign: 'center' }}>Tipo</th>
                <th>Origen</th>
                <th style={{ textAlign: 'right' }}>Cantidad</th>
                <th style={{ textAlign: 'right', color: 'var(--me-text-4)', fontWeight: 500 }}>Fecha</th>
              </tr>
            </thead>
            <tbody>
              {filtrados.map((m, i) => {
                const info = TIPO_LABELS[m.tipo] ?? { label: m.tipo, pill: 'me-pill-neutral', signo: '+' }
                return (
                  <tr key={m.id} style={{ background: i % 2 !== 0 ? 'var(--me-surface-2)' : 'transparent' }}>
                    <td>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{m.producto?.nombre ?? '—'}</div>
                      {m.notas && <div style={{ fontSize: 11, color: 'var(--me-text-4)' }}>{m.notas}</div>}
                    </td>
                    <td style={{ fontSize: 13, color: 'var(--me-text-3)' }}>
                      {m.deposito?.nombre ?? '—'}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`me-pill ${info.pill}`} style={{ fontSize: 10 }}>{info.label}</span>
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--me-text-4)' }}>
                      {REF_LABELS[m.referencia_tipo ?? ''] ?? m.referencia_tipo ?? '—'}
                    </td>
                    <td style={{ textAlign: 'right' }}>
                      <span style={{
                        fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"',
                        fontWeight: 700, fontSize: 14,
                        color: info.signo === '+' ? 'var(--me-ok)' : 'var(--me-bad)',
                      }}>
                        {info.signo}{Number(m.cantidad).toLocaleString('es-AR')}
                      </span>
                    </td>
                    <td style={{ textAlign: 'right', fontSize: 12, fontFamily: 'var(--me-mono)', color: 'var(--me-text-4)' }}>
                      {new Date(m.created_at).toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: '2-digit' })}
                      {' '}
                      {new Date(m.created_at).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

function KpiCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="me-card" style={{ minWidth: 140 }}>
      <div style={{ fontSize: 11, color: 'var(--me-text-4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 6 }}>{label}</div>
      <div style={{ fontFamily: 'var(--me-mono)', fontFeatureSettings: '"tnum"', fontWeight: 700, fontSize: 24, color: color ?? 'var(--me-text)' }}>
        {value}
      </div>
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      {[...Array(6)].map((_, i) => (
        <div key={i} style={{ height: 44, background: 'var(--me-surface-3)', borderRadius: 8, opacity: 1 - i * 0.15 }} />
      ))}
    </div>
  )
}
